import Pointer from "poin"
import Objdict from "objdict"
import Speedtest from "@frog/source/tests/utility/Speedtest.js"
import Sequence from "@frog/source/models/Sequence.js"
import Keyb from "keyb"
import Joyp from "joyp"
import Stage from "@frog/source/models/Stage.js"
import Navigation from "@frog/source/models/Navigation.js"
import Geometry from "@frog/source/utility/Geometry.js"
import clipboard from "copy-to-clipboard"
import Director from "@frog/source/juice/Director.js"
import World from "@frog/source/models/World.js"
import Zone from "@frog/source/models/Zone.js"
import Dev from "@frog/source/models/admin/Dev.js"
import App from "@frog/source/models/App.js"
import {getTime} from "@frog/source/juice/utility/Time.js"
import Unit from "@frog/source/models/Unit.js"
import Audiomix from "@frog/source/juice/Audiomix.js"
import sizes from "@frog/source/data/sizes.js"

const MINIMUM_SELECTION_DURATION = 250 // in milliseconds.
const MAXIMUM_HOVERED_POSITIONS = 10

function calculateRelativePosition({x, y}, dom) {
    if(dom == undefined) return {"x": 0, "y": 0}
    const bounds = dom.getBoundingClientRect()
    x = ((x - bounds.left) / bounds.width) * sizes.frame.width
    y = ((y - bounds.top) / bounds.height) * sizes.frame.height

    return {x, y}
}

function accountForCamera({x, y}, camera) {
    return {
        "x": (x * camera.position.z) + camera.position.x,
        "y": (y * camera.position.z) + camera.position.y
    }
}

function convertCartesianToIsometric({x, y}) {
    return {
        "x": ((x / (sizes.grid.width / 2)) + (y / (sizes.grid.height / 2))) / 2,
        "y": ((y / (sizes.grid.height / 2)) - (x / (sizes.grid.width / 2))) / 2,
        "z": 0,
    }
}

function ortho2iso({x, y}) {
    return {
        "x": x - y,
        "y": (x + y) * 0.5,
    }
}

function iso2ortho({x, y}) {
    return {
        "x": (2 * y + x) * 0.5,
        "y": (2 * y - x) * 0.5,
    }
}

function convertCartesianToTiledCoordinates(position) {
    position.x /= sizes.grid.width
    position.y /= sizes.grid.height
    // position.x += 0 //sizesAnchor.x
    // position.y -= 0 // sizesAnchor.y // HOLD UP IS THIS DOING ANYTHING?? THIS REQUIRES THE ANCHOR TO BE 0.5 & 1, not "50%" & "100%"
    // position.x = Math.floor(position.x)
    // position.y = Math.floor(position.y)
    // position.z = 1
    return position
}

// const GOLDEN_RATIO = 1.618
// const BLOCK_HEIGHT = 1 / GOLDEN_RATIO // ~0.618
// const BLOCK_HEIGHT = sizes.grid.depth / sizes.grid.height // 0.875
// const BLOCK_HEIGHT = sizes.grid.height / sizes.grid.depth // 1.142
// const BLOCK_HEIGHT = 0.95 // Not sure how to get this magical number. I just fudged it until the cursor lit up good.
// i still think depth / height is most correct (0.875), but our tiles are a little taller than they are along the grid.
// const BLOCK_HEIGHT = 1

function raycast(stage, position) {
    let castposition = {
        "x": position.x,
        "y": position.y,
        "z": position.z,
    }
    Objdict.forEach(stage.world.terrain, (terrain) => {
        // if(terrain.position.z == 0) {
        //     return
        // }
        const z = terrain.position.z + terrain.size.z
        const weirdOffset = 1 - (z * 0.5) // no idea why this works. i just found that i have to add diff amounts here. and it is inverse to the size of the terrain.
        // z=3, weirdOffset is -0.5
        // z=2, weirdOffset is 0
        // z=1, weirdOffset is +0.5
        // z=0, weirdOffset is +1

        const superposition = {
            "x": Math.floor(position.x + z + weirdOffset),
            "y": Math.floor(position.y + z + weirdOffset),
            "z": Math.floor(position.z + z),
        }
        if(superposition.x == terrain.position.x
        && superposition.y == terrain.position.y) {
            castposition = superposition
        }
    })
    return castposition
}

export default class Selection {
    constructor(that) {
        this.chapter = that.chapter

        this.selectedUnitKey = undefined
        this.multiselectedUnitKeys = new Set()
        this.hoveredUnitKey = undefined
        this.hoveredAction = undefined

        this.position = {"x": 0, "y": 0}
        this.previousPositions = []

        this.camera = {
            "type": sizes.camera.type,
            "position": this.calculateNextCameraPosition(this.chapter.stage),
        }

        this.watchedUnits = {}

        this.pointer = {"position": {"x": sizes.frame.width / 2, "y": sizes.frame.height / 2}}

        this.hasMovedMouse = false
        document.addEventListener("mousemove", (event) => {
            this.hasMovedMouse = true
        })

    }
    update(delta) {
        const previousPointerPosition = {
            "x": this.pointer.position.x,
            "y": this.pointer.position.y,
            "z": this.pointer.position.z,
        }

        this.pointer.speed = 0.4
        const DEADZONE = 0.1
        let x = Joyp.getAxis(0, "left-stick-x")
        let y = Joyp.getAxis(0, "left-stick-y")
        x = Math.max(Math.abs(x) - DEADZONE, 0) * Math.sign(x)
        y = Math.max(Math.abs(y) - DEADZONE, 0) * Math.sign(y)
        this.pointer.position.x += x * this.pointer.speed
        this.pointer.position.y += y * this.pointer.speed
        if(x != 0 || y != 0) {
            this.pointer.mode = "gamepad"
        }

        const moveDistance = (Dev.inProtoMode == true && Dev.mode == "geography") ? 0.5 : 1

        const GAMEPAD_COOLDOWN = 150
        this.gamepadCooldown = this.gamepadCooldown || 0
        this.gamepadCooldown -= delta.ms

        if(Keyb.isDown("<meta>") != true
        && Keyb.isDown("<control>") != true) {
            if(Keyb.wasJustPressed("A")
            || Keyb.wasJustPressed("<left>")) {
                this.pointer.position.x -= moveDistance / this.camera.position.z
                this.pointer.mode = "keyboard"
            }
            if(Joyp.wasJustPressed(0, "west-dbutton", delta.ms)
            || Joyp.isPressed(0, "west-dbutton", delta.ms) && this.gamepadCooldown <= 0) {
                this.pointer.position.x -= moveDistance / this.camera.position.z
                this.gamepadCooldown = GAMEPAD_COOLDOWN
                this.pointer.mode = "gamepad"
            }
            if(Keyb.wasJustPressed("D")
            || Keyb.wasJustPressed("<right>")) {
                this.pointer.position.x += moveDistance / this.camera.position.z
                this.pointer.mode = "keyboard"
            }
            if(Joyp.wasJustPressed(0, "east-dbutton", delta.ms)
            || Joyp.isPressed(0, "east-dbutton", delta.ms) && this.gamepadCooldown <= 0) {
                this.pointer.position.x += moveDistance / this.camera.position.z
                this.gamepadCooldown = GAMEPAD_COOLDOWN
                this.pointer.mode = "gamepad"
            }
            if(Keyb.wasJustPressed("W")
            || Keyb.wasJustPressed("<up>")) {
                this.pointer.position.y -= moveDistance / this.camera.position.z
                this.pointer.mode = "keyboard"
            }
            if(Joyp.wasJustPressed(0, "north-dbutton", delta.ms)
            || Joyp.isPressed(0, "north-dbutton", delta.ms) && this.gamepadCooldown <= 0) {
                this.pointer.position.y -= moveDistance / this.camera.position.z
                this.gamepadCooldown = GAMEPAD_COOLDOWN
                this.pointer.mode = "gamepad"
            }
            if(Keyb.wasJustPressed("S")
            || Keyb.wasJustPressed("<down>")) {
                this.pointer.position.y += moveDistance / this.camera.position.z
                this.pointer.mode = "keyboard"
            }
            if(Joyp.wasJustPressed(0, "south-dbutton", delta.ms)
            || Joyp.isPressed(0, "south-dbutton", delta.ms) && this.gamepadCooldown <= 0) {
                this.pointer.position.y += moveDistance / this.camera.position.z
                this.gamepadCooldown = GAMEPAD_COOLDOWN
                this.pointer.mode = "gamepad"
            }

            if(Dev.inProtoMode) {
                if(Keyb.isDown("W")) {
                    this.camera.position.y -= 0.5
                    this.pointer.mode = "keyboard"
                }
                if(Keyb.isDown("S")) {
                    this.camera.position.y += 0.5
                    this.pointer.mode = "keyboard"
                }
                if(Keyb.isDown("A")) {
                    this.camera.position.x -= 0.5
                    this.pointer.mode = "keyboard"
                }
                if(Keyb.isDown("D")) {
                    this.camera.position.x += 0.5
                    this.pointer.mode = "keyboard"
                }
            }
        }

        if(this.hasMovedMouse == true) {
            this.hasMovedMouse = false
            this.pointer.mode = "mouse"
            this.pointer.oldmouseposition = this.pointer.newmouseposition
            this.pointer.newmouseposition = calculateRelativePosition(Pointer.position, document.getElementById("frame"))
            if(Geometry.equals(this.pointer.oldmouseposition, this.pointer.newmouseposition) == false) {
                this.pointer.position = this.pointer.newmouseposition
            }
        }

        if(this.camera.type == "isometric") {
            this.position = this.pointer.position
            this.position = accountForCamera(this.position, this.camera)
            this.positionBeforeProjection = this.position
            this.position = convertCartesianToIsometric(this.position)
            this.position = raycast(this.chapter.stage, this.position)
            // this.position = Geometry.roundToWholeNumber(this.position)
        } else if(this.camera.type == "top-down") {
            let nextPosition = this.pointer.position
            nextPosition = convertCartesianToTiledCoordinates(nextPosition)
            nextPosition = accountForCamera(nextPosition, this.camera)
            if(Dev.inProtoMode == true
            && Dev.mode == "geography") {
                nextPosition = Geometry.roundToWholeOrHalf(nextPosition)
                if(Dev.devSelection.selectedTileTemplate != undefined) {
                    nextPosition = Geometry.roundToWholeNumber(nextPosition)
                } else if(Dev.devSelection.selectedVertexKey != undefined
                || Dev.devSelection.selectedEncounterAsset != undefined) {
                    nextPosition = Geometry.roundToHalfpoint(nextPosition)
                }
            } else {
                nextPosition = Geometry.roundToWholeNumber(nextPosition)
            }

            if(Dev.inProtoMode != true) {
                if(this.pointer.mode == "mouse" || this.pointer.mode == undefined) {
                    this.position = nextPosition
                } else if(this.pointer.mode == "keyboard" || this.pointer.mode == "gamepad") {
                    if(Zone.isInBounds(this.chapter.stage.encounters[this.chapter.stage.currentEncounterKey].zone, nextPosition)) {
                        this.position = nextPosition
                    } else {
                        this.pointer.position = previousPointerPosition
                    }
                }
            } else {
                this.position = nextPosition
            }
        }
        if(Dev.inProtoMode == false) {
            this.position.z = this.chapter.stage.encounters[this.chapter.stage.currentEncounterKey].z
        }

        if(Dev.inProtoMode == false
        && Zone.isInBounds(this.chapter.stage.encounters[this.chapter.stage.currentEncounterKey].zone, this.position) == false) {
            this.position = {"x": Infinity, "y": Infinity}
            this.hoveredUnitKey = undefined
            this.hoveredTimestamp = undefined
            this.hoveredEncounterSlug = undefined
            this.hoveredAction = undefined
        }

        const nextPosition = this.calculateNextCameraPosition()
        if(Dev.inProtoMode == false
        && nextPosition != undefined) {
            const CAMERA_SPEED = 0.2
            const MIN_CAMERA_DELTA = 0.01
            const CAMERA_AXES = ["x", "y", "z"]
            CAMERA_AXES.forEach((axis) => {
                if(this.camera.position[axis] < nextPosition[axis]) {
                    this.camera.position[axis] += (nextPosition[axis] - this.camera.position[axis]) * CAMERA_SPEED
                    if(this.camera.position[axis] > nextPosition[axis]) {
                        this.camera.position[axis] = nextPosition[axis]
                    }
                } else if(this.camera.position[axis] > nextPosition[axis]) {
                    this.camera.position[axis] += (nextPosition[axis] - this.camera.position[axis]) * CAMERA_SPEED
                    if(this.camera.position[axis] < nextPosition[axis]) {
                        this.camera.position[axis] = nextPosition[axis]
                    }
                }
            })
        }
        this.camera.zone = {
            "x": this.camera.position.x,
            "y": this.camera.position.y,
            "z": this.camera.position.z,
            "width": sizes.frame.width,
            "height": sizes.frame.height,
        }
        this.camera.renderzone = {
            "x": this.camera.zone.x - 1,
            "y": this.camera.zone.y - 1,
            "width": this.camera.zone.width + 1,
            "height": this.camera.zone.height + 1,
        }

        this.charge = this.charge || 0
        this.maxcharge = 400 // in milliseconds
        if(Joyp.isPressed(0, "north-button")
        || Keyb.isPressed("<enter>")) {
            this.charge += delta.ms
        } else {
            this.charge = 0
        }
        if(this.charge >= this.maxcharge) {
            if(Director.isPerforming != true) {
                App.game.chapter.endPlayerTurn()
            } else {
                if(App.unskippable != true
                && App.sequence != undefined) {
                    Sequence.skipall(App.sequence)
                    if(App.sequence.steps.length == 0) {
                        App.sequence = undefined
                    }
                }
            }
            this.charge = 0
        }

        if(this.chapter?.stage?.flags["Cutscene"] == true) {
            return
        }

        if(Dev.inProtoMode == false
        && App.getActiveStage().dialogue != undefined) {
            this.unselect()
            return
        }

        let isNewlyHovered = false
        if(this.previousPositions[0] == undefined
        || this.previousPositions[0].x != this.position.x
        || this.previousPositions[0].y != this.position.y) {
            if(World.isOutOfBounds(this.chapter.stage.world, this.position) == false
            && Zone.isInBounds(this.chapter.stage.encounters[this.chapter.stage.currentEncounterKey].zone, this.position)) {
                this.previousPositions.unshift({"x": this.position.x, "y": this.position.y, "z": this.position.z})
                if(this.previousPositions.length > MAXIMUM_HOVERED_POSITIONS) {
                    this.previousPositions.pop()
                }
                isNewlyHovered = true
                this.hoveredTime = getTime().ms
            }
        }

        const encounter = Stage.findEncounterAtPosition(this.chapter.stage, this.position)
        if(encounter != undefined) {
            this.hoveredEncounterSlug = encounter.slug
        } else {
            this.hoveredEncounterSlug = undefined
        }

        if(Dev.inProtoMode == true) Dev.devSelection.update()

        if(Dev.inProtoMode == true
        || Zone.isInBounds(this.chapter.stage.encounters[this.chapter.stage.currentEncounterKey].zone, this.position, true) == true) {
            const unitKey = (Stage.findUnitByPosition(this.modedStage, this.position) || {}).key

            if(this.hoveredUnitKey != unitKey) {
                this.hoveredUnitKey = unitKey

                if(this.hoveredUnitKey == undefined) {
                    this.hoveredTimestamp = undefined
                } else {
                    this.hoveredTimestamp = Date.now()
                }
            }
        }

        if(Dev.inProtoMode == false
        && this.canAction(this.selectedUnitKey, this.chapter.stage)) {
            this.hoveredAction = this.findAction({
                "unit": this.selectedUnit,
                "position": this.position
            })
        } else {
            this.hoveredAction = undefined
        }

        // // DEBUG
        if(this.hoveredAction != undefined
        && this.hoveredAction.omegastage.nextTurn == undefined) {
            const mark = Speedtest.mark("Actions", "Selection.update")
            // Stage.calculateNextTurn(this.hoveredAction.omegastage)
            // Stage.calculateNextTurn(this.hoveredAction.omegastage, {"ignoreAI": true})
            mark()//.log({"maximum": 100})
        }

        if(Dev.inProtoMode == false
        && this.hoveredUnit != undefined
        && this.hoveredUnit.actionmap == undefined) {
            this.hoveredUnit.actionmap = Unit.calculateActions(this.hoveredUnit.key, this.chapter.stage)
        }

        if(Keyb.wasJustPressed("<space>")
        || Joyp.wasJustPressed(0, "south-button", delta.ms)) {
            this.start()
        }
        if(Keyb.wasJustPressed("<space>")
        || Joyp.wasJustPressed(0, "south-button", delta.ms)) {
            this.finish()
        }
        if(Keyb.wasJustPressed("<escape>", delta.ms)
        || Joyp.wasJustPressed(0, "east-button", delta.ms)) {
            this.cancel()
        }

        if(isNewlyHovered == true) {
            Audiomix.trigger("Selection:Move")
            // if(this.selectedUnitKey != undefined
            // && this.hoveredAction != undefined) {
            //     Audiomix.trigger("Selection:Move")
            // } else if(this.hoveredUnitKey != undefined) {
            //     Audiomix.trigger("Selection:Move")
            // }
        }

        if(Pointer.wasJustPressed("auxiliary", true, delta.ms)) {
            const position = this.position.x + "x" + this.position.y // + "x" + this.position.z
            clipboard(position)
            console.log(position)
        }

        // if(Pointer.wasJustPressed("primary", true, delta.ms)
        // || Joyp.wasJustPressed(0, "south-button", delta.ms + 100)) {
        //     if(this.chapter.stage.units[this.hoveredUnitKey] != undefined
        //     && this.chapter.stage.units[this.hoveredUnitKey].affiliation == "bad") {
        //         this.watchedUnits[this.hoveredUnitKey] = !this.watchedUnits[this.hoveredUnitKey]
        //     }
        // }

        if(Dev.inProtoMode == false
        && this.selectedUnit != undefined
        && this.selectedUnit.skills != undefined) {
            Object.values(this.selectedUnit.skills).forEach((skillKey, index) => {
                if(Keyb.wasJustPressed(index + 1)) {
                    this.selectedSkillKey = skillKey
                }
            })
        }

        if(this.selectedUnit != undefined) {
            if(Keyb.wasJustPressed("1")) {
                this.selectedSubclassIndex = "1"
            }
            if(Keyb.wasJustPressed("2")) {
                this.selectedSubclassIndex = "2"
            }
        }

        if(App.game.chapter.stage.prompt != undefined) {
            const prompt = App.game.chapter.stage.prompt(App.game.chapter.stage)
            if(prompt.onClick != undefined) {
                if(Joyp.wasJustPressed(0, "start-button", delta.ms)) {
                    prompt.onClick()
                    this.evacuateCharge += delta.ms
                }
            }
        }
    }
    select(unitKeys) {
        if(unitKeys instanceof Set) {
            for(const unitKey of unitKeys) {
                if(this.modedStage.units[unitKey] != undefined) {
                    this.multiselectedUnitKeys.add(unitKey)
                    if(this.selectedUnitKey != undefined) this.multiselectedUnitKeys.add(this.selectedUnitKey)
                    this.selectedUnitKey = unitKey
                }
            }
        } else {
            const unitKey = unitKeys
            if(Keyb.isPressed("<shift>")) {
                if(this.multiselectedUnitKeys.has(unitKey)) {
                    this.multiselectedUnitKeys.delete(unitKey)
                    if(this.selectedUnitKey == unitKey) this.selectedUnitKey = undefined
                } else {
                    this.multiselectedUnitKeys.add(unitKey)
                    if(this.selectedUnitKey != undefined) this.multiselectedUnitKeys.add(this.selectedUnitKey)
                    this.selectedUnitKey = unitKey
                }
            } else {
                this.selectedUnitKey = unitKey
                this.multiselectedUnitKeys.forEach((unitKey) => {
                    this.modedStage.units[unitKey].previousMultiselect = structuredClone(this.multiselectedUnitKeys)
                })
                this.multiselectedUnitKeys.clear()
            }
        }
        this.clearSelected()
    }
    unselect() {
        // console.log("UNSELECTED")
        this.selectedUnitKey = undefined
        this.multiselectedUnitKeys.forEach((unitKey) => {
            this.modedStage.units[unitKey].previousMultiselect = structuredClone(this.multiselectedUnitKeys)
        })
        this.multiselectedUnitKeys.clear()
        this.clearSelected()
    }
    clearSelected() {
        this.selectedTime = getTime().ms
        this.selectedSkillKey = undefined
        this.selectedItemKey = undefined
        this.selectedItem = undefined
        this.selectedProtoUnitKey = undefined
        this.selectedSubclassIndex = undefined
        Dev.devSelection.clearSelected()
    }
    reset() {
        this.unselect()
    }
    start() {
        // Dev.devSelection.start()
        // Navigation.state.section = undefined

        if(this.chapter.phase == "explore") {
            const stage = this.chapter.stage
            const encounter = stage.encounters[stage.currentEncounterKey] // ACTUAL ENCOUNTER, NOT TO BE CONFUSED WITH CHAPTER.
            if(this.hoveredEncounterSlug != undefined
            && (stage.currentEncounterKey) != this.hoveredEncounterSlug) {
                stage.currentEncounterKey = this.hoveredEncounterSlug
                return
            }
        }
        if(this.chapter.phase == "prepare"
        || this.chapter.phase == "play") {
            if(this.selectedItemKey == undefined) {
                if(this.selectedUnitKey == undefined) {
                    if(this.canSelect(this.hoveredUnitKey)) {
                        this.select(this.hoveredUnitKey)
                        Audiomix.trigger("Selection:Start")
                        // console.log("START")
                    }
                } else if(this.selectedUnitKey != undefined) {
                    if(this.hoveredAction != undefined
                    && Action.isNothing(this.hoveredAction) != true) {
                        return
                    }
                    // You click on another selectable unit, you select
                    // that unit instead. Note that this is blocked by
                    // action tiles, so it'd rather invoke a selected
                    // skill on that unit rather than select it.
                    if(this.hoveredUnitKey != undefined
                    && this.hoveredUnitKey != this.selectedUnitKey
                    && this.canSelect(this.hoveredUnitKey)
                    && this.hoveredUnit.affiliation != "bad"
                    && this.hoveredUnit.type != "plant") {
                        // During the prepare phase, selecting
                        // another unit while you have another
                        // already selected swaps their positions.
                        if(this.chapter.phase == "prepare") {
                            if(this.selectedUnit.type == "plant" || this.hoveredUnit.type == "plant") {
                                return
                            }
                            const hoveredPosition = this.hoveredUnit.position
                            this.hoveredUnit.position = this.selectedUnit.position
                            this.selectedUnit.position = hoveredPosition
                        }

                        if(Dev.inProtoMode == true
                        && this.selectedUnitKey != undefined) {
                            const unitStack = Stage.findUnitsByPosition(this.modedStage, this.position).map((unit) => unit.key)
                            if(unitStack.includes(this.selectedUnitKey) == true) {
                                this.hoveredAction = undefined
                                const nextUnitKey = unitStack[unitStack.indexOf(this.selectedUnitKey) + 1]
                                this.select(nextUnitKey)
                                Audiomix.trigger("Selection:Start")
                                return
                            }
                        }
                        // console.log("RESTART TYPE 1")
                        this.select(this.hoveredUnitKey)
                        Audiomix.trigger("Selection:Start")
                        return
                    }
                }
            }
        }
    }
    finish() {
        Dev.devSelection.finish()

        // PROTO-UNIT-DEPLOY INTERACTIONS
        if(this.chapter.phase == "prepare") {
            if(this.selectedProtoUnitKey != undefined) {
                const partyunit = player.party.units[this.selectedProtoUnitKey]
                if(partyunit != undefined) {

                    const stage = this.chapter.stage
                    if(Stage.hasCollision({"stage": stage, "position": this.position}) == false) {
                        this.unselect()
                        stage.units[partyunit.key] = Unit.instantiate({
                            "unit": {
                                "key": partyunit.key,
                                "jobKey": partyunit.jobKey,
                                "health": partyunit.health,
                                "position": this.position,
                            }
                        })
                    }
                }
            }
        }
        // DEV - MAP EDITING INTERACTIONS
        if(this.hoveredUnitKey == undefined) {
            if(Dev.selectedTemplate == undefined) {
                Dev.lastEmptyPositionClicked = this.position
            }
        }
        // ITEM INTERACTIONS
        if(this.selectedItemKey != undefined
        && this.chapter.stage.player.inventory.items[this.selectedItemKey] != undefined) {
            let item = this.chapter.stage.player.inventory.items[this.selectedItemKey]
            if(items[item.class] != undefined) {
                item = Objdict.merge(item, items[item.class])
            }

            if(item.interact instanceof Function) {
                if(this.chapter.saveStage instanceof Function) this.chapter.saveStage()
                const interaction = item.interact({"encounter": this.chapter, "position": this.position})

                if(interaction != undefined) {
                    if(interaction.consumeItem == true) {
                        if(this.chapter.stage.player.inventory.items[item.key].amount > 1) {
                            this.chapter.stage.player.inventory.items[item.key].amount -= 1
                        } else {
                            delete this.chapter.stage.player.inventory.items[item.key]
                            this.unselect()
                        }
                    }
                }

                if(this.chapter.phase == "play"
                && this.chapter.recalculateStage instanceof Function) {
                    this.chapter.recalculateStage()
                }

                return
            }
        }
        // UNIT TALKING INTERACTIONS
        // TODO: Is it alright that unit-click-interactions are hardcoded to just the explore phase?
        if(this.chapter.phase == "explore") {
            if(this.selectedItemKey == undefined
            && this.hoveredUnitKey != undefined
            && this.canSelect(this.hoveredUnitKey)) { // TODO: Is this the right logic for what units can be talked to?
                Chapter.triggerEvent(this.chapter, "select:" + this.chapter.stage.units[this.hoveredUnitKey].classname)
                return
            }
        }
        // UNIT INTERACTIONS
        if(this.selectedUnitKey != undefined
        && getTime().ms - this.selectedTime >= MINIMUM_SELECTION_DURATION) {
            // UNIT REPOSITION-DURING-DEPLOY INTERACTIONS
            if(this.chapter.phase == "prepare") {
                if(World.isEmptyPosition(this.chapter.stage.world, this.position)) {
                    return
                }
                if(this.selectedUnit.type == "plant"
                && this.chapter.stage.world.blocks[this.position.x + "x" + this.position.y + "x" + 0] != undefined
                && this.chapter.stage.world.blocks[this.position.x + "x" + this.position.y + "x" + 0].type != "plot") {
                    return
                }
                const collidingUnit = Stage.findUnitByPosition(this.chapter.stage, this.position)
                if(collidingUnit != undefined
                && collidingUnit.affiliation == "bad") {
                    return
                }
                if(collidingUnit != undefined
                && collidingUnit.type == "plant"
                && collidingUnit.key != this.selectedUnit.key) {
                    return
                }

                this.selectedUnit.position = this.position
                this.unselect()
                return
            }
            // UNIT COMBAT INTERACTIONS
            // // You click on a unit to select it, you click on a unit
            // // again to unselect it. This is important for mobile.
            // if(this.selectedUnitKey == this.hoveredUnitKey) {
            //     // // console.log("CANCEL: ON SELF")
            //     this.selectedUnitKey = undefined
            //     this.selectedTime = undefined
            //     Audiomix.trigger("Selection:Cancel")
            //     return
            // }
            // You click on an action tile, you perform that action.
            if(this.hoveredAction != undefined
            && Action.isNothing(this.hoveredAction) != true
            && Dev.isFreezingPlaying() != true) {
                const hoveredAction = this.hoveredAction

                const actionStub = {
                    "type": hoveredAction.type,
                    "skillKey": hoveredAction.skillKey,
                    "performerKey": hoveredAction.performerKey,
                    "position": hoveredAction.position,
                    "performerPosition": hoveredAction.omegastage.units[hoveredAction.performerKey].position,
                    "distance": hoveredAction.distance,
                }
                Dev.actionTrace.push(actionStub)
                Dev.recordSavestate("livePlaytest")

                const savestateEvents = hoveredAction.steps.filter((step) => step.name == "SavestateCapture")
                savestateEvents.forEach((savestateEvent) => Dev.recordSavestate(savestateEvent.savestateSlug, savestateEvent.data))

                this.hoveredAction = undefined
                const omegastage = this.chapter.performAction(hoveredAction)
                if(this.canSelect(this.selectedUnitKey, omegastage) == false) {
                    this.selectedItem = undefined
                    this.selectedSkillKey = undefined
                    this.selectedSubclassIndex = undefined
                    this.unselect()
                }
                this.selectedItem = undefined
                this.selectedSkillKey = undefined
                this.selectedSubclassIndex = undefined
                // console.log("FINISH")
                // console.log("Selected Action", this.hoveredAction)

                Audiomix.trigger("Selection:Finish")
                return
            }
            // You click on another selectable unit, you select
            // that unit instead. Note that this is blocked by
            // action tiles, so it'd rather invoke a selected
            // skill on that unit rather than select it.
            if(this.hoveredUnitKey != undefined
            && this.canSelect(this.hoveredUnitKey)) {
                if(Dev.inProtoMode == true
                && this.selectedUnitKey != undefined) {
                    const unitStack = Stage.findUnitsByPosition(this.modedStage, this.position).map((unit) => unit.key)
                    if(unitStack.includes(this.selectedUnitKey) == true) {
                        this.hoveredAction = undefined
                        if(unitStack.indexOf(this.selectedUnitKey) + 1 < unitStack.length) {
                            const nextUnitKey = unitStack[unitStack.indexOf(this.selectedUnitKey) + 1]
                            this.select(nextUnitKey)
                            Audiomix.trigger("Selection:Start")
                            return
                        } else {
                            this.unselect()
                        }
                    }
                }
                // console.log("RESTART TYPE 2")
                this.hoveredAction = undefined
                this.select(this.hoveredUnitKey)
                Audiomix.trigger("Selection:Start")
                return
            }
            // You click where there are no units or
            // action tiles, you unselect the unit.
            if(this.hoveredAction == undefined
            || Action.isNothing(this.hoveredAction) == true) {
                // console.log("CANCEL: ON NOTHING")
                this.hoveredAction = undefined
                this.unselect()
                Audiomix.trigger("Selection:Cancel")
            }
        }
    }
    doubleStart() {
        if(this.selectedUnitKey != undefined) {
            this.select(this.modedStage.units[this.selectedUnitKey].previousMultiselect)
        }
    }
    cancel() {
        if(this.selectedSkillKey != undefined) {
            this.hoveredAction = undefined
            this.selectedItem = undefined
            this.selectedSkillKey = undefined
            this.selectedSubclassIndex = undefined
            Audiomix.trigger("Selection:Cancel")
        } else if(this.selectedUnitKey != undefined) {
            this.hoveredAction = undefined
            this.unselect()
            Audiomix.trigger("Selection:Cancel")
        }

        if(this.selectedItemKey != undefined
        || this.selectedProtoUnitKey != undefined) {
            this.unselect()
            Audiomix.trigger("Selection:Cancel")
        }
    }
    isSelected(unit) {
        return unit != undefined
            && this.selectedUnitKey == unit.key
    }
    canSelect(unitKey, stage) {
        const unit = this.modedStage.units[unitKey]
        if(unitKey == undefined
        || unit == undefined) {
            return false
        }
        if(this.chapter.phase == "prepare") {
            if(unit.affiliation == "bad") {
                return false
            }
        }
        return unitKey != undefined
            && unit != undefined
            && unit.isDead != true
            && unit.hasBeenRemoved != true
            && ((unit.type != "zone" && unit.type != "spot") || Dev.isFreezingPlaying() == true)
            // && World.isOutOfBounds(this.chapter.stage.world, unit.position) == false
            // && Zone.isInBounds(this.chapter.stage.encounters[this.chapter.stage.currentEncounterKey].zone, this.position) == true
            // && unit.affiliation == PLAYER_AFFILIATION
    }
    canAction(unitKey, stage) {
        stage = stage || this.chapter.stage
        const unit = stage.units[unitKey]
        return unitKey != undefined
            && unit != undefined
            && unit.affiliation == stage.player.affiliation
    }
    canSelectUnitSkill(unit, skill) {
        return unit != undefined
            // && unit.energy["invoke skill"] == true
            && unit.energy["2"] !== false
    }
    get selectedUnit() {
        return this.modedStage.units[this.selectedUnitKey]
    }
    get hoveredUnit() {
        return this.modedStage.units[this.hoveredUnitKey]
    }
    get activeUnitKey() {
        return this.hoveredUnitKey || this.selectedUnitKey
    }
    get activeUnit() {
        return this.selectedUnit || this.hoveredUnit
    }
    get activeSkillKey() {
        return this.hoveredSkillKey || this.selectedSkillKey
    }
    get activeItem() {
        return this.hoveredItem || this.selectedItem
    }
    get modedStage() {
        if(Dev.inProtoMode == true) return Dev.loadedProtostage
        return this.chapter.stage
    }
    mode(unit) {
        if(unit == undefined) {
            return "none"
        }
        if(this.activeSkillKey != undefined) {
            return "invoke skill only"
        }

        // if(unit.energy["move"] == true) {
        //     return "move only"
        // } else if(unit.energy["invoke skill"] == true) {
        //     return "invoke skill only"
        // }

        // if(unit.energy["move"] == false
        // && unit.energy["invoke skill"] == true) {
        //     return "invoke skill only"
        // } else if(unit.energy["move"] == true) {
        //     return "move only"
        // }

        // // NOT CHESS
        if(unit.energy != undefined) {
            if(unit.energy["move"] == false
            && unit.energy["invoke skill"] == true) {
                return "invoke skill only"
            } else if(unit.energy["move"] == true) {
                return "move and invoke skill"
            }
        }

        // if(unit.energy["move"] == false
        // && unit.energy["invoke skill"] == true) {
        //     return "invoke skill only"
        // } else if(unit.energy["move"] == true) {
        //     return "move only"
        // }
        // if(unit.energy["move"] == true) {
        //     return "move only"
        // }
        // if(unit.energy["invoke skill"] == true) {
        //     return "invoke skill only"
        // }

        return "none"
    }
    isSelectedSkill(skillKey) {
        if(this.selectedItemKey != undefined) return
        if(skillKey != undefined) {
            if(this.selectedSkillKey != undefined) {
                return skillKey == this.selectedSkillKey
            }
        }
    }
    isSelectedItemSkill(item, skillKey) {
        return skillKey != undefined
            && item != undefined
            && this.selectedSkillKey != undefined
            && this.selectedItem != undefined
            && skillKey == this.selectedSkillKey
            && item == this.selectedItem
    }
    // the difference between findActionInActionlistToActuallyUse and
    // findActionInActionlist is one is static and doesn't bother itself
    // with the currently selected unit, previous hover positions, etc.
    // I think the non-static one also concerns itself with move through
    static findActionInActionlistToActuallyUse({unit, actionlist, options = {}}) {
        const mode = options.mode || "none"

        let subactionlist = actionlist.filter((action) => {
            if(action.type == "invoke skill") {
                // // TODO: You should also check if this is
                // // a player unit and that the unit has
                // // this skill and is selected.
                // if(this.activeSkillKey != undefined
                // && options.ignoreActiveSkill != true) {
                //     if(action.skillKey != this.activeSkillKey) {
                //         return false
                //     }
                // } else {
                //     if(action.targetAffiliation != unit.affiliation
                //     && action.skillKey != unit.equippedSkills["attack1"]) {
                //         return false
                //     }
                //     if(action.targetAffiliation == unit.affiliation
                //     && action.skillKey != unit.equippedSkills["support1"]) {
                //         return false
                //     }
                // }
            }
            if(mode == "invoke skill only") {
                return (action.type == "invoke skill")
                    && Geometry.equals(action.positions[1], unit.position)
            }
            if(mode == "move only") {
                return action.type == "move"
                // || action.type == "wait"
                // || action.type == "wait-for-attack"
            }
            if(mode == "move and invoke skill") {
                return true
            }
        })

        subactionlist.forEach((action) => {
            action.recentlyHoveredScore = Infinity
            if(action.type == "move through"
            || action.type == "no move") {
                return action
            }
            if(action.positions != undefined
            && action.positions.length > 1) {
                // for(let index in this.previousPositions) {
                //     let previousPosition = this.previousPositions[index]
                //     if(Geometry.equals({"x": previousPosition.x, "y": previousPosition.y}, {"x": action.positions[1].x, "y": action.positions[1].y})) { // IGNORING THE Z AXIS
                //     // if(Geometry.equals(previousPosition, action.positions[1])) {
                //         action.recentlyHoveredScore = index
                //         return action
                //     }
                // }
            }
            // if(action.type == "move") {
            //     action.recentlyHoveredScore = -10
            // }
        })
        subactionlist.sort((a, b) => {
            if(a.recentlyHoveredScore < b.recentlyHoveredScore) return -1
            if(a.recentlyHoveredScore > b.recentlyHoveredScore) return +1
            if(a.distance < b.distance) return -1
            if(a.distance > b.distance) return +1
            return 0
        })
        const moveActionlist = subactionlist.filter((action) => {
            return action.type == "move"
            // || action.type == "wait"
            // || action.type == "wait-for-attack"
        })
        if(moveActionlist[0] != undefined) {
            return moveActionlist[0]
        }
        if(subactionlist[0] != undefined) {
            return subactionlist[0]
        }
    }
    findSubactionsInActionlist({unit, actionlist, options = {}}) {
        const mode = options.mode || this.mode(unit)

        let subclassIndex = undefined
        if(unit.subclasses != undefined) {
            if(this.selectedSubclassIndex != undefined) {
                subclassIndex = this.selectedSubclassIndex
            } else if(unit.energy["1"] != false) {
                subclassIndex = "1"
            } else {
                subclassIndex = "2"
            }
        }

        let subactionlist = actionlist.filter((action) => {
            if(action.type.includes("invoke skill")
            && this.activeItem != undefined
            && this.activeItem != action.item) {
                return false
            }
            if(action.type.includes("invoke skill")
            && options.ignoreActiveSkill != true
            && this.activeSkillKey != undefined
            && this.activeSkillKey != action.skillKey) {
                return false
            }
            // if(mode == "move and invoke skill"
            // || mode == "invoke skill only") {
            //     if(action.type == "invoke skill") {
            //         // TODO: You should also check if this is
            //         // a player unit and that the unit has
            //         // this skill and is selected.
            //         if(this.activeSkillKey != undefined
            //         && options.ignoreActiveSkill != true) {
            //             if(action.skillKey != this.activeSkillKey) {
            //                 return false
            //             }
            //         } else {
            //             return true
            //             // if(action.targetAffiliation != unit.affiliation
            //             // && action.skillKey != unit.equippedSkills["attack1"]) {
            //             //     return false
            //             // }
            //             // if(action.targetAffiliation == unit.affiliation
            //             // && action.skillKey != unit.equippedSkills["support1"]) {
            //             //     return false
            //             // }
            //         }
            //     }
            // }
            if(subclassIndex != undefined
            && subclassIndex != action.subclassIndex) {
                return false
            }
            if(mode == "invoke skill only") {
                return (action.type == "invoke skill" || action.type == "invoke skill - can not target")
                    && Geometry.equals(action.positions[1], unit.position)
            }
            if(mode == "move only") {
                return action.type == "move"
                    || action.type == "move through"
                    // || action.type == "wait"
                    // || action.type == "wait-for-attack"
            }
            if(mode == "move and invoke skill") {
                return true
            }
        })

        subactionlist.forEach((action) => {
            action.recentlyHoveredScore = Infinity
            if(action.type == "move through"
            || action.type == "no move") {
                return action
            }
            if(action.positions != undefined
            && action.positions.length > 1) {
                for(let index in this.previousPositions) {
                    let previousPosition = this.previousPositions[index]
                    if(Geometry.equals({"x": previousPosition.x, "y": previousPosition.y}, {"x": action.positions[1].x, "y": action.positions[1].y})) { // IGNORING THE Z AXIS
                        action.recentlyHoveredScore = index
                        return action
                    }
                }
            }
        })
        subactionlist.sort((a, b) => {
            if(a.priority > b.priority) return -1
            if(a.priority < b.priority) return +1
            if(a.recentlyHoveredScore < b.recentlyHoveredScore) return -1
            if(a.recentlyHoveredScore > b.recentlyHoveredScore) return +1
            if(a.distance < b.distance) return -1
            if(a.distance > b.distance) return +1
            return 0
        })
        return subactionlist
    }
    findActionInActionlist({unit, actionlist, options = {}}) {
        return this.findSubactionsInActionlist({unit, actionlist, options})[0]
    }
    findAction({unit, position}) {
        if(unit == undefined
        || unit.actionmap == undefined
        || unit.actionmap[position.x + "x" + position.y/* + "x" + position.z*/] == undefined
        || unit.actionmap[position.x + "x" + position.y/* + "x" + position.z*/] instanceof Array == false) {
            return
        }
        const actionlist = unit.actionmap[position.x + "x" + position.y/* + "x" + position.z*/]
        return this.findActionInActionlist({unit, actionlist})
    }
    calculateNextCameraPosition(stage) {
        // return {
        //     "x": -1 * sizes.grid.width * sizesAnchor.x,
        //     "y": -1 * sizes.grid.height * sizesAnchor.y,
        //     "z": 0
        // }

        // return {
        //     "x": 0, "y": 0, "z": 0
        // }

        stage = stage || App.getActiveStage()
        if(stage == undefined) return

        if(stage.encounters[stage.currentEncounterKey] == undefined) return {"x": 0, "y": 0, "z": 0}
        const currentZone = stage.encounters[stage.currentEncounterKey]

        const position = {}

        position.x = currentZone.x
        position.y = currentZone.y
        position.z = currentZone.zoomout ?? 1

        position.x += currentZone.width / 2
        position.y += currentZone.height / 2

        if(stage.camera != undefined
        && stage.camera.position != undefined) {
            position.x = stage.camera.position.x + 0.5
            position.y = stage.camera.position.y + 0.5
            position.z = stage.camera.position.z
        }

        if(stage?.camera?.unitKey != undefined
        && stage.units[stage.camera.unitKey] != undefined) {
            position.x = stage.units[stage.camera.unitKey].position.x + 0.5
            position.y = stage.units[stage.camera.unitKey].position.y + 0.5
            position.z = stage.camera.zoomout ?? 0.5
        }

        position.x -= (sizes.frame.width / 2) * position.z
        position.y -= (sizes.frame.height / 2) * position.z

        // if(position.x < 0) position.x = 0
        // if(position.y < 0) position.y = 0
        // if(position.x > stage.world.width - sizes.frame.width) position.x = stage.world.width - sizes.frame.width
        // if(position.y > stage.world.height - sizes.frame.height) position.y = stage.world.height - sizes.frame.height

        return position

        // // stolen from views/utility/pixel.js
        // const nextPosition = {}
        // // nextPosition.x = (position.x - position.y) * (sizes.grid.width / 2),
        // // nextPosition.y = ((position.x + position.y) * (sizes.grid.height / 2)) - (position.z * sizes.grid.depth),
        // nextPosition.x = position.x
        // nextPosition.y = position.y
        //
        // // nextPosition.x -= sizes.canvas.width / 2
        // // nextPosition.y -= sizes.canvas.height / 2
        // // nextPosition.x -= sizes.grid.width * sizesAnchor.x // Why is this commented out? Why don't we use this??
        // // nextPosition.y -= sizes.grid.height * sizesAnchor.y
        // // nextPosition.y -= 7 // for artist's touch.
        //
        // return nextPosition
    }
    isOnCamera(position) {
        const BUFFER = 2
        if(position.x < Math.floor(this.camera.position.x / sizes.grid.width) - BUFFER
        || position.x > Math.floor(this.camera.position.x / sizes.grid.width) + (sizes.frame.width * this.camera.position.z) + BUFFER
        || position.y < Math.floor(this.camera.position.y / sizes.grid.height) - BUFFER
        || position.y > Math.floor(this.camera.position.y / sizes.grid.height) + (sizes.frame.height * this.camera.position.z) + BUFFER) {
            return false
        }
        return true
    }
    centerPositionInCamera(position) {
        this.camera.position.x = (position.x - (sizes.frame.width * this.camera.position.z) / 2 + 0.5) * sizes.grid.width
        this.camera.position.y = (position.y - (sizes.frame.height * this.camera.position.z) / 2 + 0.5) * sizes.grid.height
    }
}

class Action {
    static isNothing(action) {
        if(action.type == "no move"
        || action.type == "wait"
        || action.type == "invoke skill - can not target"
        || action.type == "move through") {
            return true
        }
        const performer = action.alphastage.units[action.performerKey]
        if(action.type == "move"
        && performer.position.x == action.position.x
        && performer.position.y == action.position.y) {
        // && performer.position.z == action.position.z) {
            return true
        }
    }
}
